import React from 'react';
import {reactCreateMemo} from '@wix/devzai-utils-react';
import type {HideableValue} from '@wix/devzai-utils-common';
import {hideableValueGetVisibleValue, urlOverrideQueryParamsForEncodedUrl} from '@wix/devzai-utils-common';
import type {WixEcomCheckoutCustomLineItem} from '@wix/devzai-common-wix';
import {WixEcomCheckoutCustomLineItemKind} from '@wix/devzai-common-wix';
import type {IDoppeViewerActionContext} from '../../doppe-sdk';
import type {DoppeViewerLang} from '../../client-server-common/doppe-viewer/doppe-viewer-lang';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import {BymoPageViewerMode, bymoPageViewerContextDataIsLivePage} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {DoppeWixCheckoutOrderDescriptionLineName} from '../../client-server-common/doppe-wix-checkout-order-validation/doppe-wix-checkout-order-validation';
import {
    DoppeViewerPaymentsDisabledModal,
    doppeViewerPaymentsDisabledIsBlocked,
} from '../doppe-viewer-payments-disabled-modal/doppe-viewer-payments-disabled-modal';
import {
    doppeResolveCheckoutContinueShoppingUrl,
    doppeViewerActionContextOpenModal,
} from '../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {DoppeActionModalIframeContent} from '../doppe-action-modal-iframe-content/doppe-action-modal-iframe-content';
import {DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {DoppeViewerBuyOrClaimDownloadModal} from './doppe-viewer-buy-or-claim-download-modal';
import {BymoPageLayout, useBymoPageLayout} from '../bymo-page-style/bymo-page-style';

/**
 * Renders the Wix Checkout iframe inside the live-preview modal, with the
 * `showMobileView` query param recomputed from the current viewer layout on
 * every layout change. Wix Checkout itself is not responsive, so when the
 * editor toggles between mobile and desktop device views we have to swap the
 * iframe URL between the mobile and desktop variants — capturing
 * `isMobileView` once at click time would leave the iframe stale.
 *
 * `useBymoPageLayout()` already subscribes to the viewport via
 * `useLayoutBinding`, so it re-fires when the preview iframe is resized by
 * the editor's device toggle.
 */
const LivePreviewCheckoutIframe = reactCreateMemo(function LivePreviewCheckoutIframe(props: {
    baseCheckoutUrl: string;
}) {
    const {baseCheckoutUrl} = props;

    const layout = useBymoPageLayout();
    const isMobileView = layout === BymoPageLayout.Narrow;

    const checkoutUrl = urlOverrideQueryParamsForEncodedUrl(baseCheckoutUrl, {
        showMobileView: isMobileView ? 'true' : null,
        disableContinueShopping: 'true',
    });

    return (
        <DoppeActionModalIframeContent
            withMinHeight={false}
            src={checkoutUrl}
        />
    );
});

/**
 * Shape of the payment-settings block used by both the top-level Download File
 * action and the `DoppeActionCTAType.DownloadFile` CTA option. Kept structural
 * (rather than imported) so this module can be the single shared click handler
 * for both call sites without dragging in CTA-button-specific types.
 */
export interface DoppeViewerDownloadFilePaymentSettings {
    productName: string;
    price: DoppeActionPrice;
    /**
     * Quantity for the custom line item. Almost always 1 — exists only to keep
     * parity with `WixEcomCheckout.CustomProduct` (Wix Checkout requires a
     * positive integer).
     */
    quantity: number;
    /**
     * Optional human-facing description shown as the item-type label in the
     * Wix Checkout order summary. When empty, the viewer falls back to a
     * localized "Digital download" label.
     */
    customItemTypeDescription: HideableValue<string>;
}

export interface DoppeViewerDownloadFileResolveOnClickOptions {
    /** Wix Media file id (Media Manager) of the file to download. */
    fileId: string;
    /** Display name of the file, used in the live-preview download toast. */
    fileName: string;
    /**
     * Hidden = free download (server mints a signed URL on click).
     * Visible = paid download (Buy/Claim modal → Wix Checkout → claim modal).
     */
    paymentSettings: HideableValue<DoppeViewerDownloadFilePaymentSettings>;
    actionContext: IDoppeViewerActionContext<any>;
    viewerLang: DoppeViewerLang;
    /**
     * Title shown on the in-iframe Wix Checkout modal in live-preview mode.
     * For the CTA-button caller this is the CTA's `text`; for the top-level
     * Download File action this is the action's outer `name`.
     */
    checkoutModalTitle: string;
    /**
     * Localized error message shown when free-URL generation fails. Pre-resolved
     * by the caller because the helper runs outside React (so it can't call the
     * `useDoppeViewerLocales` hook itself).
     */
    freeDownloadGenericErrorMessage: string;
    /**
     * Localized fallback label for the Wix Checkout custom line item's
     * `itemTypeDescription` when the configured description is empty.
     * Same hook-context reason as above.
     */
    digitalDownloadFallbackItemTypeLabel: string;
}

/**
 * Resolves the onClick handler for a "Download File" CTA — shared between the
 * top-level `doppeActionTypeDownloadFile` action and the legacy
 * `DoppeActionCTAType.DownloadFile` CTA option.
 *
 * Free path: server mints a signed URL → browser navigates (live) or emits a
 * `eventDownloadFilePrevented` toast (live preview).
 *
 * Paid path: payments-disabled gate → Buy modal → on "Buy" opens Wix Checkout
 * with a custom line item tagged with the action id via the `Reference`
 * descriptionLine. After checkout the buyer is redirected back with
 * `iframeModalURL`, which triggers the native thank-you modal — that modal
 * is now responsible for the post-purchase Download button + auto-unlock
 * (no separate claim flow). `kind: Custom` is required (never
 * `DigitalProduct`) because metasites with Wix Stores installed reject
 * DIGITAL-preset custom line items with `MISSING_DIGITAL_FILE`; see
 * `docs/wix-checkout-api/wix-checkout-api.md`.
 *
 * Errors on the free path surface as a notification toast; paid-path errors
 * after checkout surface from the thank-you modal.
 */
export function doppeViewerDownloadFileResolveOnClick(
    options: DoppeViewerDownloadFileResolveOnClickOptions,
): () => Promise<void> {
    const {
        fileId,
        fileName,
        paymentSettings: hideablePaymentSettings,
        actionContext,
        viewerLang,
        checkoutModalTitle,
        freeDownloadGenericErrorMessage,
        digitalDownloadFallbackItemTypeLabel,
    } = options;

    const paymentSettings = hideableValueGetVisibleValue(hideablePaymentSettings, null);

    return async () => {
        await actionContext.performContextCallback(async ({clientServices, viewerContextData}) => {
            const actionId = actionContext.action.id;
            const pageId = bymoPageViewerContextDataIsLivePage(viewerContextData)
                ? viewerContextData.bymoPageId
                : '';
            const isLivePreviewMode = viewerContextData.mode === BymoPageViewerMode.LivePreview;

            if (paymentSettings === null) {
                if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
                    try {
                        const previewResult = await clientServices.getApiClient().generateFreeDownloadFileUrl({
                            pageId: pageId,
                            actionId: actionId,
                            mediaFileId: fileId,
                        });

                        viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent(
                            'eventDownloadFilePrevented',
                            {
                                url: previewResult.downloadUrlInfo.url,
                                fileName: fileName,
                            },
                        );
                    } catch {
                        viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent(
                            'eventActionPrevented',
                            {},
                        );
                    }

                    return;
                }

                try {
                    const result = await clientServices.getApiClient().generateFreeDownloadFileUrl({
                        pageId: pageId,
                        actionId: actionId,
                        mediaFileId: fileId,
                    });

                    window.location.href = result.downloadUrlInfo.url;
                } catch (error) {
                    console.error('[DoppeViewerDownloadFileCta] Free download URL generation failed:', error);
                    actionContext.showNotification({
                        message: freeDownloadGenericErrorMessage,
                    });
                }
                return;
            }

            if (doppeViewerPaymentsDisabledIsBlocked(actionContext.getBymoPageContextProps().premiumFeaturesStatus)) {
                actionContext.modalsController.showInnerModal({
                    content: ({modalController}) => (
                        <DoppeViewerPaymentsDisabledModal
                            closeCallback={() => modalController.closeWindow()}
                        />
                    ),
                });
                return;
            }

            const customItemTypeDescription = hideableValueGetVisibleValue(paymentSettings.customItemTypeDescription, '');
            const sitePaymentCurrency = actionContext.getBymoPageContextProps().sitePaymentCurrency ?? null;
            const effectivePrice = sitePaymentCurrency !== null
                ? {
                    ...paymentSettings.price,
                    currencyCode: sitePaymentCurrency,
                }
                : paymentSettings.price;

            const openWixCheckoutForDownloadFile = async () => {
                if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
                    viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent(
                        'eventWixEcomCheckoutAttempted',
                        {},
                    );
                }

                const customLineItem: WixEcomCheckoutCustomLineItem.Custom = {
                    kind: WixEcomCheckoutCustomLineItemKind.Custom,
                    productName: paymentSettings.productName,
                    price: String(paymentSettings.price.actualPrice),
                    quantity: paymentSettings.quantity,
                    itemTypeDescription: customItemTypeDescription || digitalDownloadFallbackItemTypeLabel,
                    descriptionLines: [
                        {
                            name: DoppeWixCheckoutOrderDescriptionLineName.Reference,
                            plainText: actionId,
                        },
                    ],
                };

                const checkoutResult = await clientServices.cartService.createCheckoutUrl({
                    customLineItems: [customLineItem],
                    catalogLineItems: [],
                    lang: viewerLang,
                    htmlSiteInfo: actionContext.getBymoPageContextProps().htmlSiteInfo,
                });

                if (checkoutResult.checkoutPageUrl) {
                    const successUrl = urlOverrideQueryParamsForEncodedUrl(window.location.href, {
                        iframeModalURL: checkoutResult.checkoutPageUrl,
                    });
                    const continueShoppingUrl = doppeResolveCheckoutContinueShoppingUrl(clientServices);
                    const checkoutUrlWithContinueShopping = urlOverrideQueryParamsForEncodedUrl(
                        checkoutResult.checkoutPageUrl,
                        {
                            successUrl: successUrl,
                            continueShoppingUrl: continueShoppingUrl,
                        },
                    );

                    if (isLivePreviewMode) {
                        doppeViewerActionContextOpenModal(
                            actionContext,
                            () => (
                                <LivePreviewCheckoutIframe
                                    baseCheckoutUrl={checkoutUrlWithContinueShopping}
                                />
                            ),
                            {
                                mobileLayout: DoppeViewerModalPanelLayout.FullScreen,
                                desktopLayout: DoppeViewerModalPanelLayout.FullScreen,
                                showHeader: true,
                                showHeaderAsCloseButton: true,
                                title: checkoutModalTitle,
                            },
                        );
                    } else {
                        window.location.href = checkoutUrlWithContinueShopping;
                    }
                }
            };

            actionContext.modalsController.showInnerModal({
                content: ({modalController}) => (
                    <DoppeViewerBuyOrClaimDownloadModal
                        actionId={actionId}
                        pageId={pageId}
                        productName={paymentSettings.productName}
                        description={customItemTypeDescription || undefined}
                        priceData={effectivePrice}
                        onBuyClick={openWixCheckoutForDownloadFile}
                        closeCallback={() => modalController.closeWindow()}
                    />
                ),
            });
        });
    };
}
